import React, { Fragment, useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Typography, Fab } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import Flashphoner from "../flashphoner.js";
import { Translate } from "orion-components/i18n";
import EventBus from "orion-components/SharedComponents/EventBus";
import PlaybackStopped from "../Wowza/utils/PlaybackStopped";

// Standard playback speeds supported by Flashphoner player
const STANDARD_SPEED_RANGE = [-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16];

const { constants } = Flashphoner;
const { SESSION_STATUS, STREAM_STATUS } = constants;
const { ESTABLISHED, DISCONNECTED, FAILED } = SESSION_STATUS;
const { PENDING, PLAYING, PLAYBACK_PROBLEM, STOPPED, NOT_ENOUGH_BANDWIDTH } = STREAM_STATUS;
// Potentially necessary to connect across different networks
// const mediaOptions = {
// 	iceServers: [
// 		{
// 			urls: "turn:turn.flashphoner.com:443?transport=tcp",
// 			username: "flashphoner",
// 			credential: "coM77EMrV7Cwhyan"
// 		}
// 	]
// };

const propTypes = {
	url: PropTypes.string.isRequired,
	playbackUrl: PropTypes.string,
	playbackUrlError: PropTypes.bool,
	startTime: PropTypes.instanceOf(Date),
	endTime: PropTypes.instanceOf(Date)
};

global.RecordedModeStartDateTime = null;
global.RecordedModeEndDateTime = null;

const defaultProps = { playbackUrl: "" };

const FlashphonerRecordedWebRTCPlayer = props => {
	const { url, playbackUrl, playbackUrlError, onSetStartAndEndTime } = props;
	const [startTime, setStartTime] = useState(null);
	const [endTime, setEndTime] = useState(null);

	const [status, setStatus] = useState(null);
	const frame = useRef(null);
	const _isMounted = useRef(false);
	// Using useRef instead of useState for session and stream
	const sessionRef = useRef(null);
	const streamRef = useRef(null);

	const playbackUrlRef = useRef(playbackUrl);
	const [stopVideoStreaming, setStopVideoStreaming] = useState(false);
	const [key, setKey] = useState(0);
	const [isPlaybackStopped, setIsPlaybackStopped] = useState(false);

	const usePrevious = value => {
		const ref = useRef(null);
		useEffect(() => {
			ref.current = value;
		}, [value]);
		return ref.current;
	};

	const prevProps = usePrevious(props);

	//force update
	const [, updateState] = useState();
	const forceUpdate = useCallback(() => updateState({}), []);

	const setPlayBackTimeFrame = value => {
		const { startDateTimeVal, endDateTimeVal } = value;
		const newStartTime = new Date(startDateTimeVal);
		const newEndTime = new Date(endDateTimeVal);
		setStartTime(newStartTime);
		setEndTime(newEndTime);
		global.RecordedModeStartDateTime = startDateTimeVal;
		global.RecordedModeEndDateTime = endDateTimeVal;
		if (onSetStartAndEndTime) {
			onSetStartAndEndTime(newStartTime, newEndTime);
		}
		componentKey++;
		setKey(componentKey);
		setStopVideoStreaming(false);
	};

	const renderNewComponentInstance = () => {
		componentKey++;
		setKey(componentKey);
		setStopVideoStreaming(false);
	};

	const reStartStoppedPlaybacStream = () => {
		const dateTime = {
			startDateTimeVal: global.RecordedModeStartDateTime,
			endDateTimeVal: global.RecordedModeEndDateTime
		};
		if (streamRef.current) {
			streamRef.current.stop();
		}

		// Restart the session and stream
		if (sessionRef.current) {
			startStream(sessionRef.current);
		} else {
			startSession();
		}
		setPlayBackTimeFrame(dateTime);
		setIsPlaybackStopped(false);
		setStopVideoStreaming(false);
	};

	const stopPlaybackStream = () => {
		const stream = streamRef.current;
		if (stream) {
			try {
				stream.stop();
				const videoElement = document.getElementById(stream.id());
				if (videoElement) {
					videoElement.style.display = "none";
				}
			} catch (error) {
				console.error("Error stopping stream:", error);
			}
		} else {
			setIsPlaybackStopped(true);
			setStatus("STOPPED");
		}
		setStopVideoStreaming(true);
		setIsPlaybackStopped(true);
		setStatus("STOPPED");
	};

	let componentKey = 0;

	useEffect(() => {
		if (startTime && endTime) {
			// Send the updated times to the parent component via callback
			onSetStartAndEndTime(startTime, endTime);
		}
	}, [startTime, endTime, onSetStartAndEndTime]);

	useEffect(() => {
		EventBus.subscribe("PlaybackRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", setPlayBackTimeFrame);
		EventBus.subscribe(
			"PlaybackRecordedWebRTCPlayerWrapper_renderNewComponentInstance",
			renderNewComponentInstance
		);
		EventBus.subscribe("Playback_stopVideoStreaming", stopPlaybackStream);
		EventBus.subscribe("Playback_restartstopVideoStreaming", reStartStoppedPlaybacStream);

		return () => {
			EventBus.unsubscribe("PlaybackRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", setPlayBackTimeFrame);
			EventBus.unsubscribe(
				"PlaybackRecordedWebRTCPlayerWrapper_renderNewComponentInstance",
				renderNewComponentInstance
			);
			EventBus.unsubscribe("Playback_stopVideoStreaming", stopPlaybackStream);
			EventBus.unsubscribe("Playback_restartstopVideoStreaming", reStartStoppedPlaybacStream);
		};
	}, []);

	useEffect(() => {
		if (!!url && (!prevProps || url !== prevProps.url)) {
			if (sessionRef.current) {
				handleRefresh();
			} else {
				startSession();
			}
		}
	}, [url]);

	useEffect(() => {
		playbackUrlRef.current = playbackUrl;
		// If there's no existing session, we won't do anything here
		if (sessionRef.current) {
			// If we have a playbackUrl and it's either the first one or changed
			if (!!playbackUrl && (!prevProps || playbackUrl !== prevProps.playbackUrl)) {
				if (streamRef.current) {
					// Existing stream, so we need to stop it first
					streamRef.current.stop();
				}

				// Start new stream
				startStream(sessionRef.current);
			}
		}
	}, [playbackUrl]);

	useEffect(() => {
		_isMounted.current = true;

		return () => {
			_isMounted.current = false;
			endSession();
		};
	}, []);

	const endSession = () => {
		if (stream) stream.stop();
		if (session) session.disconnect();
	};

	const startSession = () => {
		if (!frame.current) {
			forceUpdate();
		}

		try {
			Flashphoner.init({
				receiverLocation: "WSReceiver2.js",
				decoderLocation: "video-worker2.js",
				preferredMediaProviders: []
			});
			Flashphoner.createSession({ urlServer: url })
				.on(ESTABLISHED, session => {
					if (_isMounted.current) {
						setStatus(session.status());
						sessionRef.current = session;

						// If we have a playbackUrl, we'll go ahead and start the stream
						// If we don't, we will start the stream when we have one.
						if (playbackUrlRef.current) {
							startStream(session);
						}
					} else {
						session.disconnect();
					}
				})
				.on(DISCONNECTED, () => {
					if (_isMounted.current) {
						setStatus("DISCONNECTED");
					}
				})
				.on(FAILED, () => {
					if (_isMounted.current) {
						setStatus("FAILED");
					}
				});
		} catch (error) {
			console.log("ERROR", error);
		}
	};

	const startStream = session => {
		const options = {
			name: playbackUrlRef.current,
			display: frame.current
		};
		const stream = session
			.createStream(options)
			.on(PENDING, () => {
				if (_isMounted.current) {
					setStatus("PENDING");
					const video = document.getElementById(stream.id());
					video.style.maxWidth = "100%";
					video.style.maxHeight = "100%";
					video.style.objectFit = "contain";
					video.style.height = "100%";
					video.style.display = "none";
				}
			})
			.on(PLAYBACK_PROBLEM, () => {
				if (_isMounted.current) {
					setStatus("FAILED");
				}
			})
			.on(PLAYING, () => {
				if (_isMounted.current) {
					setStatus("PLAYING");
					const video = document.getElementById(stream.id());
					video.style.display = "block";

					// Only publish when the stream is playing
					EventBus.publish("RecordedBottomBar_startVideo", true);
					// Publish player capabilities after RecordedBottomBar is ready
					console.debug(
						`[FlashphonerRecordedWebRTCPlayer] Publishing capabilities (PLAYING event):`,
						STANDARD_SPEED_RANGE
					);
					EventBus.publish("VideoPlayer_CapabilitiesReady", {
						instanceId: props.instanceId,
						supportedSpeeds: STANDARD_SPEED_RANGE
					});
				} else {
					stream.stop();
				}
			})
			.on(STOPPED, () => {
				if (_isMounted.current) {
					setStatus("STOPPED");
				}
			})
			.on(STREAM_STATUS.FAILED, () => {
				if (_isMounted.current) {
					setStatus("FAILED");
				}
			})
			.on(NOT_ENOUGH_BANDWIDTH, () => {
				if (_isMounted.current) {
					setStatus("LOW BANDWIDTH");
				}
			});

		stream.play();
		streamRef.current = stream;
	};

	const handleRefresh = e => {
		if (e) {
			e.stopPropagation();
		}
		endSession();
		setStatus(null);
		startSession();
	};

	const styles = {
		wrapper: {
			display: "flex",
			position: "absolute",
			alignItems: "center",
			flex: "auto",
			flexDirection: "column",
			justifyContent: "center"
		},
		button: {
			zIndex: 99,
			marginTop: 12
		}
	};

	const statusIndicator = () => {
		if (isPlaybackStopped) {
			// Render PlaybackStopped component when the stream is stopped
			return <PlaybackStopped />;
		} else if (status === "FAILED") {
			return (
				<Fragment>
					<Typography align="center" variant="subtitle1">
						<Translate value="global.CBComponents.video.webRTCPlayer.camProblem" />
					</Typography>
					<Fab style={styles.button} color="primary" onClick={handleRefresh} size="small">
						<Refresh style={{ color: "#FFF" }} />
					</Fab>
				</Fragment>
			);
		} else if (playbackUrlError) {
			return (
				<Typography align="center" variant="subtitle1">
					<Translate value="global.CBComponents.video.webRTCPlayer.unableToAccess" />
				</Typography>
			);
		} else if (status === "PLAYING") {
			return null;
		} else {
			return <CircularProgress />;
		}
	};

	return (
		<div ref={frame} className="camera-text-wrapper" style={styles.wrapper}>
			{statusIndicator()}
		</div>
	);
};

FlashphonerRecordedWebRTCPlayer.propTypes = propTypes;
FlashphonerRecordedWebRTCPlayer.defaultProps = defaultProps;

export default FlashphonerRecordedWebRTCPlayer;
